<template>
  <v-select :model-value="modelValue" :items="items" :label="label" :variant="variant"
            :multiple="multiple" :placeholder="placeholder" density="compact" hide-details
            @update:model-value="$emit('update:modelValue', $event)">
    <!-- No múltiplo, um chip por escolhido, com um respiro entre eles -->
    <template #selection="{item}">
      <ontos-colored-value :option="item" form="chip" :class="{'mr-1': multiple}"/>
    </template>
    <template #item="{item, props}">
      <v-list-item v-bind="props" :title="undefined">
        <ontos-colored-value :option="item" form="chip"/>
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import {ColoredOption} from '@/types/ColoredOption'

/**
 * Escolher um degrau de uma escada: as opções como chips, da mais fraca à mais forte.
 *
 * Nasceu só para os níveis de acesso de um ambiente, e virou genérico no segundo consumidor — a tela
 * de convites, cuja escolha é idêntica trocando as opções. A diferença entre os usos é a moldura e
 * a lista, e as duas vêm por prop. Os papéis do ambiente o usam no `multiple`, que não são escada,
 * mas são opções coloridas de uma lista curta.
 *
 * **Chip, e não bolinha nem régua**: o que se escolhe aqui é opção de um domínio fechado e curto,
 * e a forma diz a natureza do valor — está escrito no `ValueForm`.
 *
 * Os slots existem porque o `v-select` desenharia o texto do item; aqui ele desenha o chip. E o
 * `item` que eles recebem **já é a opção**, não o embrulho do Vuetify: no `VSelect` o slot é
 * chamado com `{item: item.raw, internalItem, index}`, e procurar `.raw` ali devolve `undefined`
 * — foi assim que o chip quebrou a primeira vez.
 */
@Component({
  name: 'ontos-colored-select',
  emits: ['update:modelValue'],
  components: {'ontos-colored-value': ColoredValue}
})
class ColoredSelect extends Vue {
  /** O valor escolhido — ou, no `multiple`, os valores. */
  @Prop({type: [String, Array], required: true}) readonly modelValue!: string | string[]
  /**
   * Vários de uma vez: os papéis de uma pessoa, ao lado do nível dela — o mesmo seletor e os mesmos
   * chips, para os dois não se desenharem de jeitos diferentes na mesma linha (pedido do usuário).
   */
  @Prop({type: Boolean, default: false}) readonly multiple!: boolean
  @Prop({type: String, default: ''}) readonly placeholder!: string
  /** As opções, já coloridas e na ordem da escada — `levelOptions()`, `accessOptions()`. */
  @Prop({type: Array, required: true}) readonly items!: Array<ColoredOption & {value: string}>
  @Prop({type: String, default: ''}) readonly label!: string
  /**
   * A moldura: `outlined` num formulário, `plain` dentro da linha de uma lista.
   *
   * Tipada com as duas que existem, e não como `string`: o `variant` do `v-select` é uma união
   * fechada, e um `string` solto aqui não casa com ela — o `vue-tsc` reclama, e com razão.
   */
  @Prop({type: String, default: 'outlined'}) readonly variant!: 'outlined' | 'plain'
}

export default toNative(ColoredSelect)
</script>
